import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

const STATUSES = ["new", "contacted", "approved", "rejected", "archived"];
const STATUS_STYLES = {
  new: "bg-[var(--cl-primary)]/20 text-[var(--cl-primary)]",
  contacted: "bg-[var(--cl-gold)]/20 text-[var(--cl-gold)]",
  approved: "bg-emerald-500/20 text-emerald-400",
  rejected: "bg-zinc-500/20 text-zinc-400",
  archived: "bg-zinc-800 text-zinc-500",
};

export default function AdminCardSubmissions() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});

  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/card-submissions")).data); }
    catch { toast.error("Failed to load submissions"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status, admin_notes) => {
    try {
      await api.put(`/admin/card-submissions/${id}`, { status, admin_notes });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await api.delete(`/admin/card-submissions/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div>
      <h1 className="font-display text-4xl mb-2">Card Submissions</h1>
      <p className="text-zinc-400 text-sm mb-6">Sellers submitting cards for review through the public form.</p>

      {rows.length === 0 && <div className="text-zinc-500 text-sm">No submissions yet.</div>}

      <div className="space-y-3" data-testid="card-submissions-list">
        {rows.map((s) => {
          const isOpen = openId === s.id;
          return (
            <div key={s.id} className="border border-white/10 rounded bg-[var(--cl-surface)]"
                 data-testid={`csm-row-${s.id}`}>
              <div className="p-4 flex flex-wrap items-start gap-4 justify-between cursor-pointer"
                   onClick={() => setOpenId(isOpen ? null : s.id)}>
                <div>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {s.email} · {s.created_at?.slice(0, 10)} · {(s.image_urls?.length || 0)} photo(s)
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] px-2 py-1 rounded uppercase tracking-widest ${STATUS_STYLES[s.status] || ""}`}>
                    {s.status}
                  </span>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>
              {isOpen && (
                <div className="border-t border-white/5 p-4 space-y-4">
                  <div>
                    <div className="text-label text-zinc-500 mb-1">Card Details</div>
                    <p className="text-sm text-zinc-300 whitespace-pre-wrap">{s.card_details}</p>
                  </div>
                  {s.image_urls?.length > 0 && (
                    <div>
                      <div className="text-label text-zinc-500 mb-2">Photos</div>
                      <div className="flex flex-wrap gap-2">
                        {s.image_urls.map((url, i) => {
                          const abs = absUrl(url);
                          return (
                            <a key={url} href={abs} target="_blank" rel="noreferrer"
                               data-testid={`csm-photo-${s.id}-${i}`}
                               className="block w-24 h-24 border border-white/10 rounded overflow-hidden hover:border-[var(--cl-orange)]">
                              <img src={abs} className="w-full h-full object-cover" alt={`card ${i+1}`} />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-label text-zinc-500 mb-2">Status</div>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((st) => (
                        <button key={st}
                                onClick={() => setStatus(s.id, st, s.admin_notes)}
                                data-testid={`csm-status-${s.id}-${st}`}
                                className={`text-xs px-3 py-1 rounded border transition-colors ${s.status === st ? "bg-[var(--cl-primary)] border-[var(--cl-primary)] text-white" : "border-white/15 text-zinc-300 hover:border-[var(--cl-orange)]"}`}>
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-label text-zinc-500 mb-1">Admin notes</div>
                    <textarea rows={3}
                              value={notesDraft[s.id] ?? s.admin_notes ?? ""}
                              onChange={(e) => setNotesDraft({ ...notesDraft, [s.id]: e.target.value })}
                              data-testid={`csm-notes-${s.id}`}
                              className="w-full bg-black border border-white/10 rounded px-3 py-2 text-sm" />
                    <button onClick={() => setStatus(s.id, s.status, notesDraft[s.id] ?? s.admin_notes)}
                            className="btn-outline text-xs mt-2">Save notes</button>
                  </div>
                  <div className="pt-4 border-t border-white/5 flex justify-end">
                    <button onClick={() => del(s.id)}
                            data-testid={`csm-delete-${s.id}`}
                            className="text-xs text-zinc-500 hover:text-[var(--cl-primary)] flex items-center gap-1">
                      <Trash2 size={12}/> Delete permanently
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
